import { useState, useCallback, createContext, useContext } from "react";

const TOKEN_KEY = "mavrion_jwt_token";
const USER_KEY = "mavrion_user";

export interface AuthUser {
  id: number;
  username: string;
  name: string | null;
}

interface AuthContextValue {
  token: string;
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
}

function getStoredToken(): string {
  return localStorage.getItem(TOKEN_KEY) || "";
}

function getStoredUser(): AuthUser | null {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

export const AuthContext = createContext<AuthContextValue>({
  token: "",
  user: null,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
});

export function useAuthState() {
  const [token, setToken] = useState(getStoredToken);
  const [user, setUser] = useState<AuthUser | null>(getStoredUser);

  const login = useCallback((t: string, u: AuthUser) => {
    localStorage.setItem(TOKEN_KEY, t);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setToken(t);
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem("sicar_jwt_token");
    localStorage.removeItem("sicar_user");
    setToken("");
    setUser(null);
  }, []);

  return {
    token,
    user,
    isAuthenticated: !!token && !!user,
    login,
    logout,
  };
}

export function useAuth() {
  return useContext(AuthContext);
}

export async function authFetch(url: string, options?: RequestInit): Promise<any> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    ...((options?.headers as Record<string, string>) || {}),
    ...(options?.body ? { "Content-Type": "application/json" } : {}),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json();
}
